import { useState } from "react";
import { Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

// Drop-in replacement for Contact.js that uses Netlify Forms instead of
// the FastAPI backend. No REACT_APP_BACKEND_URL or server needed.
// Rename this file to Contact.js to use it, and view submissions under
// Site configuration -> Forms in the Netlify dashboard.

const encode = (data) =>
  Object.keys(data)
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`)
    .join("&");

export default function Contact() {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    message: "",
  });

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.email || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }

    setLoading(true);
    try {
      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({ "form-name": "contact", ...form }),
      });
      toast.success("Message sent! We'll be in touch shortly.");
      setForm({ first_name: "", last_name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 lg:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left: Info */}
          <div className="space-y-6">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0047AB] mb-3">
                Contact
              </p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900">
                Let's Talk
              </h2>
            </div>
            <p className="text-base text-slate-600 leading-relaxed max-w-md">
              Have a question or a project in mind? Drop us a message and we'll be in
              touch to discuss how we can help.
            </p>

            <div className="pt-4 space-y-4">
              <div className="border border-slate-200 rounded-lg p-5 bg-slate-50">
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">
                  Email
                </p>
                <a
                  href="mailto:info@digitalblue.com.au"
                  data-testid="contact-email-link"
                  className="text-[#0047AB] font-medium text-sm hover:underline"
                >
                  info@digitalblue.com.au
                </a>
              </div>
              <div className="border border-slate-200 rounded-lg p-5 bg-slate-50">
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">
                  Location
                </p>
                <p className="text-slate-700 font-medium text-sm">
                  Adelaide, South Australia
                </p>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div>
            <form
              name="contact"
              method="POST"
              data-netlify="true"
              netlify-honeypot="bot-field"
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="border border-slate-200 rounded-xl p-8 bg-white space-y-5"
            >
              {/* Required hidden fields for Netlify's build-time form detection */}
              <input type="hidden" name="form-name" value="contact" />
              <p className="hidden">
                <label>
                  Don't fill this out: <input name="bot-field" />
                </label>
              </p>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name" className="text-sm font-medium text-slate-700">
                    First Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="first_name"
                    name="first_name"
                    data-testid="contact-first-name-input"
                    value={form.first_name}
                    onChange={handleChange}
                    placeholder="John"
                    className="border-slate-200 focus:ring-2 focus:ring-[#0047AB] focus:border-transparent"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name" className="text-sm font-medium text-slate-700">
                    Last Name
                  </Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    data-testid="contact-last-name-input"
                    value={form.last_name}
                    onChange={handleChange}
                    placeholder="Doe"
                    className="border-slate-200 focus:ring-2 focus:ring-[#0047AB] focus:border-transparent"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                  Email <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  data-testid="contact-email-input"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="john@company.com"
                  className="border-slate-200 focus:ring-2 focus:ring-[#0047AB] focus:border-transparent"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-sm font-medium text-slate-700">
                  Contact Number
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  data-testid="contact-phone-input"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="+61 400 000 000"
                  className="border-slate-200 focus:ring-2 focus:ring-[#0047AB] focus:border-transparent"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-sm font-medium text-slate-700">
                  Message <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  data-testid="contact-message-input"
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Tell us about your project or question..."
                  rows={5}
                  className="border-slate-200 focus:ring-2 focus:ring-[#0047AB] focus:border-transparent resize-none"
                  required
                />
              </div>

              <Button
                type="submit"
                data-testid="contact-submit-button"
                disabled={loading}
                className="w-full bg-[#0047AB] hover:bg-[#003585] text-white rounded-md py-6 text-sm font-medium transition-all hover:scale-[0.99] inline-flex items-center justify-center gap-2"
              >
                {loading ? "Sending..." : "Send Message"}
                {!loading && <Send size={16} />}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
